import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import { useEffect, useState } from "react";
import {
  AutocompleteInput,
  Button,
  Datagrid,
  FunctionField,
  List,
  ReferenceInput,
  SimpleList,
  TextField,
  useGetList,
} from "react-admin";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { checkPermission } from "server/security/utils";
import { CURRICULUMS, DISCIPLINES, MOBILE_BREAKPOINT, StudentServicePath, TOPICS } from "utils/constants";
import BackButtonToolbar from "features/BackButtonToolbar";
import { EmptyWithAction } from "server/components";
import CreateTopicButton from "./components/CreateTopicButton";
import CustomFilter from "features/CustomFilter";
import { ALL_OPTIONS } from "features/filters/constants";
import dataProvider from "server/dataProvider";
import SecuredComponent from "server/security/SecuredComponent";
import EditIcon from "@mui/icons-material/Edit";
import { studentRequests } from "api/studentApi";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

const Topics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const curriculumName = location.state.curriculumName;
  const { id } = useParams();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const canCreate = checkPermission(getPermissions(), permissions.canManageCurriculumDisciplinesTopics);

  const [appliedAcademicYear, setAppliedAcademicYear] = useState(null);
  const [academicYearId, setAcademicYearId] = useState(null);

  const { data: academicYears } = useGetList(studentRequests.getAcademicYearsSelect(), ALL_OPTIONS);

  // The first year is picked and applied right away.
  useEffect(() => {
    if (!academicYearId && academicYears?.length) {
      setAcademicYearId(academicYears[0].id);
      setAppliedAcademicYear(academicYears[0].id);
    }
  }, [academicYearId, academicYears]);
  const [disciplineId, setDisciplineId] = useState(null);
  const [excludeIds, setExcludeIds] = useState(null);

  useEffect(() => {
    setDisciplineId(null);
  }, []);

  useEffect(() => {
    const fetchExcludeIds = async () => {
      try {
        const { data } = await dataProvider.customRequest(
          studentRequests.getCurriculumTopicsDisciplineIds(id, { academicYearId: appliedAcademicYear }),
          null,
          "GET",
        );
        setExcludeIds(data);
      } catch (err) {
        console.error(err);
      }
    };
    if (appliedAcademicYear && id) {
      fetchExcludeIds();
    }
  }, [appliedAcademicYear, id]);

  const toolbarFilters = [
    <ReferenceInput
      source="disciplineId"
      reference={studentRequests.getCurriculumDisciplinesSelect(id)}
      filter={{ filterByUser: true }}
      alwaysOn
    >
      <AutocompleteInput
        label="Дисциплина"
        filterToQuery={q => (q?.length > 2 ? { disciplineName: q } : {})}
        value={disciplineId || ""}
        helperText={false}
        sx={{ width: "300px" }}
        onChange={e => setDisciplineId(e.target.value || null)}
      />
    </ReferenceInput>,
  ];

  return (
    <>
      <BackButtonToolbar />
      <CustomFilter
        title={`Филтър теми за упражнения на Учебен план: ${curriculumName}`}
        cardWidth={isMobile ? "100%" : "500px"}
        filters={[
          {
            label: "Учебна година",
            type: "select",
            value: academicYearId ?? "",
            setValue: setAcademicYearId,
            choices: academicYears ?? [],
          },
        ]}
        buttonLabel="Покажи"
        buttonDisabled={!academicYearId}
        buttonOnClick={() => setAppliedAcademicYear(academicYearId)}
      />
      {appliedAcademicYear && (
        <List
          resource={`${StudentServicePath}${CURRICULUMS}/${id}/${TOPICS}/${DISCIPLINES}`}
          title={`Теми за учебен план: ${curriculumName}`}
          empty={
            <EmptyWithAction message="Няма намерени резултати.">
              <CreateTopicButton
                canCreate={canCreate}
                id={id}
              />
            </EmptyWithAction>
          }
          actions={
            <CreateTopicButton
              id={id}
              canCreate={canCreate}
              excludeIds={excludeIds}
            />
          }
          filter={{ academicYearId: appliedAcademicYear }}
          filters={toolbarFilters}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={() => (
                <MobileListRow fields={[{ label: "Дисциплина", value: <TextField source="disciplineName" /> }]} />
              )}
              secondaryText={record => (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  <SecuredComponent permission={permissions.canManageCurriculumDisciplinesTopics}>
                    <Button
                      label="Редакция"
                      startIcon={<EditIcon />}
                      onClick={() =>
                        navigate(`/${StudentServicePath}${TOPICS}/${CURRICULUMS}/${id}/edit`, {
                          state: {
                            disciplineName: record.disciplineName,
                            disciplineId: record.disciplineId,
                            academicYearId: appliedAcademicYear,
                          },
                        })
                      }
                    />
                  </SecuredComponent>
                </div>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={{
                "& .RaDatagrid-headerCell": {
                  textAlign: "left",
                },
              }}
            >
              <TextField
                source="disciplineName"
                label="Дисциплина"
              />
              <SecuredComponent permission={permissions.canManageCurriculumDisciplinesTopics}>
                <FunctionField
                  render={rec => (
                    <Button
                      label="Редакция"
                      startIcon={<EditIcon />}
                      onClick={() =>
                        navigate(`/${StudentServicePath}${TOPICS}/${CURRICULUMS}/${id}/edit`, {
                          state: {
                            disciplineName: rec.disciplineName,
                            disciplineId: rec.disciplineId,
                            academicYearId: appliedAcademicYear,
                          },
                        })
                      }
                    />
                  )}
                />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default Topics;
